import { FC, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { createPortal } from "react-dom";

import Text from "@/components/Text";

const Triangle = styled.div`
  width: 26px;
  height: 14px;
  position: absolute;
  overflow: hidden;
  top: -14px;
  left: 12px;
  &::after {
    content: "";
    position: absolute;
    width: 14px;
    height: 14px;
    background: ${({ theme }) => theme.colors.white};
    transform: rotate(45deg);
    top: 8px;
    left: 5px;
    box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

interface BoxProps {
  $left: number;
  $top: number;
}

const Box = styled.div<BoxProps>`
  position: fixed;
  padding: 8px 12px;
  z-index: 0;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  top: ${({ $top }) => $top + 35}px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  ${({ $left }) =>
    css`
      left: calc(${$left}px - 9px);
    `}
`;

const StyledText = styled(Text)`
  display: flex;
  align-items: center;
`;

const Dot = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color};
  margin-right: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
`;

const modalRoot = document.getElementById("modals-root");

interface Props {
  hideTooltip: () => void;
  domRect?: DOMRect;
  text: string;
  color: string;
}

const Tooltip: FC<Props> = ({ hideTooltip, text, color, domRect }) => {
  const ref = useRef(document.createElement("div"));

  useEffect(() => {
    const callback = () => hideTooltip();
    window.addEventListener("scroll", callback, true);
    return () => {
      window.removeEventListener("scroll", callback, true);
    };
  }, [hideTooltip]);

  useEffect(() => {
    const el = ref.current;
    modalRoot!.appendChild(el);
    return () => {
      modalRoot!.removeChild(el);
    };
  }, []);

  if (!domRect) return null;

  return createPortal(
    <Box $left={domRect.x} $top={domRect.y + window.scrollY}>
      <Triangle />
      <StyledText $type="extraSmall">
        <Dot $color={color} />
        {text}
      </StyledText>
    </Box>,
    ref.current
  );
};

export default Tooltip;
